import { useState } from 'react';
import { AccountCreditPolicyPanel } from '../components/AccountCreditPolicy';
import { RainCheckPolicyPanel } from '../components/RainCheckPolicy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, IntegrationFieldState, IntegrationProviderState, ProcessingOffsetSetting } from '../api';
import { settingsEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, ErrorNote, Field, Panel, formatWhen } from '../ui';
import { INPUT } from './event/shared';

/**
 * Settings → Integrations (D-50). Every third-party key lives here, test and
 * live side by side, with one switch per provider for which set is in use.
 * Secrets are write-only: the page shows that a key is set, its last four
 * characters and when it changed, never the key.
 */

type Mode = 'TEST' | 'LIVE';
const MODES: Mode[] = ['TEST', 'LIVE'];

/**
 * The switches, and what each one does (item 172, D-306).
 *
 * Every flag is listed whether or not anybody has touched it, with the
 * difference between "off because we chose that" and "off because nobody has
 * been here" said out loud -- otherwise somebody re-decides a decision that was
 * already made.
 *
 * A flag that is not safe to flip mid-sale says so **on the control**, not in a
 * confirmation afterward. The one that exists today is offline scanning: turning
 * it off during a door would strand a scanner that is already offline and cannot
 * be told.
 */
function FlagsPanel() {
  const queryClient = useQueryClient();
  const flags = useQuery({ queryKey: ['settings', 'flags'], queryFn: settingsEndpoints.flags });
  const [error, setError] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) => settingsEndpoints.setFlag(key, enabled),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['settings', 'flags'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  return (
    <Panel
      title="Behavior switches"
      description="Things that can be turned on and off without a deploy. Each one says what it does and whether it is safe to change while people are buying."
    >
      {error ? (
        <div className="px-5 pt-4">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      <div className="divide-y divide-line">
        {(flags.data?.flags ?? []).map((flag) => (
          <div key={flag.key} className="flex flex-wrap items-start justify-between gap-4 px-5 py-4">
            <div className="max-w-2xl">
              <p className="text-sm font-medium text-ink-900">
                {flag.name}
                {flag.liveSafe ? null : <span className="ml-2 text-xs font-normal text-warn">Not safe to change mid-sale</span>}
              </p>
              <p className="mt-0.5 text-xs text-ink-600">{flag.description}</p>
              <p className="mt-1 font-mono text-xs text-ink-400">{flag.key}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-ink-500">
                {flag.overridden ? `Set ${flag.enabled ? 'on' : 'off'}` : `Default (${flag.default ? 'on' : 'off'})`}
              </span>
              <Button
                variant={flag.enabled ? 'primary' : 'secondary'}
                disabled={set.isPending}
                onClick={() => {
                  if (!flag.liveSafe && !window.confirm(`${flag.name} is not safe to change while people are buying.\n\n${flag.description}\n\nChange it anyway?`)) return;
                  set.mutate({ key: flag.key, enabled: !flag.enabled });
                }}
              >
                {flag.enabled ? 'On' : 'Off'}
              </Button>
            </div>
          </div>
        ))}
        {(flags.data?.flags ?? []).length === 0 ? <p className="px-5 py-4 text-sm text-ink-500">No switches are declared.</p> : null}
      </div>
    </Panel>
  );
}

export default function SettingsPage() {
  const can = usePermissions();
  const integrations = useQuery({ queryKey: ['settings', 'integrations'], queryFn: settingsEndpoints.integrations });
  if (!can('settings.manage')) return <NotPermitted permission="settings.manage" />;
  if (integrations.error) return <ErrorNote>{(integrations.error as ApiError).message}</ErrorNote>;
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-ink-900">Integrations</h1>
        <p className="mt-1 text-sm text-ink-600">
          API keys for the services this platform talks to. Each provider keeps a test set and a live set; the switch says which one the site uses.
          {integrations.data?.production ? '' : ' This environment is development: it can hold live keys but will only ever run in test mode.'}
        </p>
      </div>
      {(integrations.data?.providers ?? []).map((provider) => (
        <ProviderPanel key={provider.provider} provider={provider} />
      ))}
      <ProcessingOffsetPanel />
      <AccountCreditPolicyPanel />
      <RainCheckPolicyPanel />
      <FlagsPanel />
    </div>
  );
}

/**
 * "Cover the processing fees?" — the rate, and whether it is offered (D-242).
 *
 * This lived only in `system_settings`, so changing 4% to 3.5% meant a
 * `wrangler d1 execute` against production. It tracks what the card networks
 * charge, which moves, and the person who knows it has moved is not the person
 * with a database console.
 */
function ProcessingOffsetPanel() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState<ProcessingOffsetSetting | null>(null);
  const query = useQuery({ queryKey: ['settings', 'processing-offset'], queryFn: settingsEndpoints.processingOffset });
  const current = draft ?? query.data?.processing_offset ?? null;

  const save = useMutation({
    mutationFn: (input: ProcessingOffsetSetting) => settingsEndpoints.updateProcessingOffset(input),
    onSuccess: (result) => {
      setError(null);
      setDraft(null);
      setNotice(result.processing_offset.offered ? `Offered at ${(result.processing_offset.basis_points / 100).toFixed(2)}%.` : 'No longer offered at checkout.');
      void queryClient.invalidateQueries({ queryKey: ['settings', 'processing-offset'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  if (!current) return null;
  /*
   * Typed as a percentage and stored as basis points. Two decimal places,
   * because 2.9% + 30c averages out to something like 3.4% and rounding that
   * to a whole number is throwing away real money in one direction or the
   * other.
   */
  const percent = (current.basis_points / 100).toFixed(2);

  return (
    <Panel
      title="Cover the processing fees"
      description="A box at checkout asking the customer to carry the card processing cost. It is a fee, never a donation: the customer receives full value for what they pay, so it is not deductible and never appears on a giving statement."
    >
      <div className="space-y-3 p-5">
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        {notice ? <p className="text-sm text-ink-600">{notice}</p> : null}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={current.offered} onChange={(e) => setDraft({ ...current, offered: e.target.checked })} className="h-4 w-4 accent-brand" />
          Offer it at checkout
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={current.default_on} disabled={!current.offered} onChange={(e) => setDraft({ ...current, default_on: e.target.checked })} className="h-4 w-4 accent-brand" />
          Start ticked
          <span className="text-ink-500">— offering it unticked is a real choice, and the more conservative one</span>
        </label>
        <Field label="Rate" htmlFor="offset-rate" hint="A percentage of the whole order, gift and delivery included, because that is what the card networks charge on.">
          <div className="flex items-center gap-2">
            <input
              id="offset-rate"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={percent}
              disabled={!current.offered}
              onChange={(e) => setDraft({ ...current, basis_points: Math.round(Number(e.target.value || 0) * 100) })}
              className={`${INPUT} w-32`}
            />
            <span className="text-sm text-ink-500">%</span>
          </div>
        </Field>
        <div className="flex items-center gap-3">
          <Button onClick={() => save.mutate(current)} disabled={save.isPending || !draft}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {draft ? <button type="button" className="text-sm text-ink-500 underline" onClick={() => { setDraft(null); setNotice(null); }}>Discard</button> : null}
        </div>
      </div>
    </Panel>
  );
}

function ProviderPanel({ provider }: { provider: IntegrationProviderState }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] });

  const switchMode = useMutation({
    mutationFn: (mode: Mode) => settingsEndpoints.updateIntegration(provider.provider, { mode }),
    onSuccess: (result) => {
      setError(null);
      setNotice(`${provider.name} is now in ${result.provider.active_mode.toLowerCase()} mode.`);
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  return (
    <Panel
      title={provider.name}
      description={provider.description}
      actions={
        <div className="flex items-center gap-2" role="radiogroup" aria-label={`${provider.name} mode`}>
          {MODES.map((mode) => {
            const active = provider.active_mode === mode;
            const disabled = mode === 'LIVE' && !provider.live_allowed;
            return (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={disabled || switchMode.isPending}
                title={disabled ? 'Live mode is only available in production' : undefined}
                onClick={() => !active && switchMode.mutate(mode)}
                className={`rounded px-3 py-1.5 text-sm font-medium ${active ? (mode === 'LIVE' ? 'bg-bad text-white' : 'bg-accent text-white') : 'border border-line bg-surface text-ink-600 hover:bg-page'} disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {mode === 'LIVE' ? 'Live' : 'Test'}
                {active ? ' · in use' : ''}
              </button>
            );
          })}
        </div>
      }
    >
      <div className="space-y-3 px-5 py-4">
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        {notice ? <p className="rounded border border-ok/30 bg-ok/10 px-3 py-2 text-sm text-ok">{notice}</p> : null}
        <div className="grid gap-4 md:grid-cols-2">
          {MODES.map((mode) => (
            <ModeColumn key={mode} provider={provider} mode={mode} onError={setError} onNotice={setNotice} onSaved={refresh} />
          ))}
        </div>
        {provider.mode_updated_at ? <p className="text-xs text-ink-400">Mode last changed {formatWhen(provider.mode_updated_at)}.</p> : null}
      </div>
    </Panel>
  );
}

function ModeColumn({ provider, mode, onError, onNotice, onSaved }: { provider: IntegrationProviderState; mode: Mode; onError: (message: string | null) => void; onNotice: (message: string | null) => void; onSaved: () => void }) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [clearing, setClearing] = useState<Set<string>>(() => new Set());
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const state = provider.modes[mode];
  const active = provider.active_mode === mode;

  const save = useMutation({
    mutationFn: () => {
      const credentials: Record<string, string | null> = {};
      for (const [key, value] of Object.entries(drafts)) if (value.trim()) credentials[key] = value.trim();
      for (const key of clearing) credentials[key] = null;
      return settingsEndpoints.updateIntegration(provider.provider, { credentials: { [mode]: credentials } });
    },
    onSuccess: (result) => {
      onError(null);
      onNotice(`Saved ${result.changed.length} change${result.changed.length === 1 ? '' : 's'} to ${provider.name} (${mode.toLowerCase()}).`);
      setDrafts({});
      setClearing(new Set());
      onSaved();
    },
    onError: (cause: ApiError) => onError(cause.message),
  });

  const test = useMutation({
    mutationFn: () => settingsEndpoints.testIntegration(provider.provider, mode),
    onSuccess: (result) => setTestResult(result),
    onError: (cause: ApiError) => setTestResult({ ok: false, message: cause.message }),
  });

  const dirty = Object.values(drafts).some((v) => v.trim()) || clearing.size > 0;
  const anyConfigured = provider.fields.some((f) => state[f.key]?.configured);

  return (
    <div className={`rounded-lg border p-4 ${active ? 'border-accent/50 bg-accent/5' : 'border-line bg-page'}`}>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold text-ink-900">
          {mode === 'LIVE' ? 'Live' : 'Test'} keys {active ? <Badge tone={mode === 'LIVE' ? 'bad' : 'accent'}>in use</Badge> : null}
        </p>
        {anyConfigured ? (
          <Button onClick={() => test.mutate()} disabled={test.isPending}>
            {test.isPending ? 'Testing…' : 'Test connection'}
          </Button>
        ) : null}
      </div>
      {testResult ? <p className={`mb-3 rounded px-3 py-2 text-sm ${testResult.ok ? 'bg-ok/10 text-ok' : 'bg-bad-soft text-bad'}`}>{testResult.message}</p> : null}
      <div className="space-y-3">
        {provider.fields.map((field) => (
          <CredentialField key={field.key} field={field} state={state[field.key]} draft={drafts[field.key] ?? ''} clearing={clearing.has(field.key)} onDraft={(value) => setDrafts((d) => ({ ...d, [field.key]: value }))} onClear={(value) => setClearing((c) => { const next = new Set(c); if (value) next.add(field.key); else next.delete(field.key); return next; })} />
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Button variant="primary" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : `Save ${mode.toLowerCase()} keys`}
        </Button>
        {dirty ? (
          <Button onClick={() => { setDrafts({}); setClearing(new Set()); }}>Discard</Button>
        ) : null}
      </div>
    </div>
  );
}

function CredentialField({ field, state, draft, clearing, onDraft, onClear }: { field: IntegrationProviderState['fields'][number]; state: IntegrationFieldState | undefined; draft: string; clearing: boolean; onDraft: (value: string) => void; onClear: (clear: boolean) => void }) {
  const id = `${field.key}-${Math.random().toString(36).slice(2, 6)}`;
  const configured = state?.configured ?? false;
  const status = !configured
    ? 'not set'
    : state?.source === 'worker_secret'
      ? `from Worker secret ${state.hint ?? ''}`
      : `set ${state?.hint ?? ''}${state?.updated_at ? ` · ${formatWhen(state.updated_at)}` : ''}`;
  return (
    <Field label={field.label} htmlFor={id} hint={field.hint}>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type={field.secret ? 'password' : 'text'}
          autoComplete="off"
          spellCheck={false}
          className={`${INPUT} flex-1 font-mono`}
          placeholder={configured ? (field.secret ? 'Replace…' : (state?.value ?? '')) : field.secret ? 'Paste the secret' : 'Paste the key'}
          value={draft}
          disabled={clearing}
          onChange={(e) => onDraft(e.target.value)}
        />
        {configured && state?.source === 'settings' ? (
          <label className="flex items-center gap-1 text-xs text-ink-600 whitespace-nowrap">
            <input type="checkbox" checked={clearing} onChange={(e) => onClear(e.target.checked)} /> clear
          </label>
        ) : null}
      </div>
      <p className={`mt-1 text-xs ${configured ? 'text-ink-600' : 'text-ink-400'}`}>
        {configured ? <Badge tone={state?.source === 'worker_secret' ? 'neutral' : 'ok'}>{status}</Badge> : status}
        {!field.secret && configured && state?.value ? <span className="ml-2 font-mono">{state.value}</span> : null}
      </p>
    </Field>
  );
}
